import { action, computed, observable, reaction } from 'mobx';
import { ViewController } from '@estee/elc-service';
import { LoyaltyRepository } from '~repositories/LoyaltyRepository';
import { LoadLoyaltyInfoUseCase } from '~use-cases/LoadLoyaltyInfoUseCase';
import { ILoyaltyLayouts } from '~views/components/interfaces';
import { LoyaltyMarketingConfig } from './ILoyaltyMarketingPage';
import { createMultiGridLayout } from '~utils/loadOnDemand';
import { ILoyaltyMarketingPageController } from '~exported/loyalty-marketing-page/ILoyaltyMarketingPageController';
import {
    IBenefitsRewardsListBenefitsItem,
    IBenefitsRewardsTierLabelItem
} from '~views/marketing-page/benefits-rewards/IBenefitsRewards';
import { ISiteConfig, IDrupalConfig, ILoyaltyConfig } from '~domain/entities/ISiteConfig';
import mergeConfigurationFiles from '~utils/mergeConfigurationFiles';
import { AnalyticalTrackingUseCase } from '~use-cases/AnalyticalTrackingUseCase';
import { eventTag } from '~utils/Analytics';

class LoyaltyMarketingPageController extends ViewController {
    @observable private configs: LoyaltyMarketingConfig;
    @observable private layouts: ILoyaltyLayouts;
    @observable private loyaltyRepository: LoyaltyRepository;

    constructor({ loyaltyRepository }: ILoyaltyMarketingPageController) {
        super();
        this.loyaltyRepository = loyaltyRepository;
        reaction(
            () => this.loyaltyRepository?.loyaltyInfo?.hasLoyalty,
            () => {
                this.updateLayout();
            }
        );
    }

    @action
    public loaded = async () => {
        this.isLoading = true;
        await this.loadConfig();
        await new LoadLoyaltyInfoUseCase().execute();
        this.isLoading = false;
    };

    @action
    private updateLayout = () => {
        const transformedConfigs = Object.keys(this.configs).reduce((accumulator, key) => {
            if (this.configs[key].params?.[key]?.hideForLoyalty && this.data.isLoyaltyUser) {
                return accumulator;
            }
            accumulator[key] = this.configs[key];

            return accumulator;
        }, {} as LoyaltyMarketingConfig);
        this.configs = transformedConfigs;
        this.layouts = createMultiGridLayout(transformedConfigs);
    };

    @action
    public async loadConfig(): Promise<void> {
        try {
            const loyaltyConfig = (window?.site as ISiteConfig)?.loyaltyConfig?.loyalty;

            const [drupalConfigData, loyaltyConfigData] = await Promise.all([
                import(`../../config/${loyaltyConfig.domain}-drupal.json`),
                import(`../../config/${loyaltyConfig.domain}-loyalty.json`)
            ]) as [IDrupalConfig, ILoyaltyConfig];

            const mergedConfig = mergeConfigurationFiles(
                drupalConfigData.drupal?.marketing?.loyaltyMarketingPageConfig || {},
                loyaltyConfigData?.marketing?.loyaltyMarketingPageConfig || {}
            );

            this.setInitialConfigs(mergedConfig);
        } catch (error) {
            console.error('Failed to load loyalty config:', error);
        }
    }

    @action
    setInitialConfigs(configs: LoyaltyMarketingConfig) {
        this.configs = configs;
        this.updateLayout();
    }

    @action
    onLinkClick(ctaLabel: string) {
        const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
            { event_name: 'account_loyalty', event_action: 'cta_click', event_label: ctaLabel },
            eventTag.LOYALTY_ACTIONS_TRACKING
        );
        LoyaltyEventTracking.execute();
    }

    @action
    public groupBenefitsByTier = (
        tierLabels: IBenefitsRewardsTierLabelItem[],
        listBenefits: IBenefitsRewardsListBenefitsItem[]
    ) => {
        const groupedBenefits: {
            benefits: IBenefitsRewardsTierLabelItem;
            labels: IBenefitsRewardsListBenefitsItem[];
        }[] = tierLabels.map((tierLabel, i) => {
            const groupedLabels: IBenefitsRewardsListBenefitsItem[] = listBenefits.flatMap(
                (benefit) => {
                    if (i === 0 && benefit.levelOne) {
                        return [benefit];
                    }
                    if (i === 1 && benefit.levelTwo) {
                        return [benefit];
                    }
                    if (i === 2 && benefit.levelThree) {
                        return [benefit];
                    }
                    if (i === 3 && benefit.levelFourth) {
                        return [benefit];
                    }

                    return [];
                }
            );

            return {
                benefits: tierLabel,
                labels: groupedLabels
            };
        });

        return groupedBenefits;
    };

    @computed
    public get data() {
        return {
            configs: this.configs,
            isLoading: this.isLoading,
            isLoyaltyUser: this.loyaltyRepository.isLoyaltyUser,
            isOnlineUser: this.loyaltyRepository.isOnlineUser,
            layouts: this.layouts,
            isSignedIn: this.loyaltyRepository.isSignedIn
        };
    }
}

export default LoyaltyMarketingPageController;
